// Line-item table reconstruction: find the header row, derive column bands
// from the header token positions, bucket each item line's tokens into those
// bands, then verify the arithmetic (sum of qty x unit vs the printed
// subtotal/total). Credit-note lines (negative amounts) are legitimate items.

import type { Field, InvoiceItem, Line } from "./types";
import { field } from "./types";
import { MONEY_RE, parseMoney } from "./fields";

type Role = "qty" | "model" | "serial" | "price";

const HEADER_WORDS: Record<Role, RegExp> = {
  qty: /^(qty|quantity|qt[eé])$/i,
  model: /^(model|item|description|product|article)s?$/i,
  serial: /^(serial|serials|s\/n|sn|s[eé]rie)(\s?(no|#|number))?\.?$/i,
  price: /^(unit)?\s?(price|amount|cost|montant|prix)$/i,
};

const SERIAL_RE = /^(?=[A-Z0-9-]*\d)[A-Z0-9][A-Z0-9-]{5,}$/;
const STOP_RE = /sub\s?-?total|sous-total|\btotal\b|\b(gst|hst|tps|tvh|tax)\b|balance|amount due/i;

interface Band { role: Role; center: number }

function headerBands(l: Line): Band[] {
  const bands: Band[] = [];
  for (const t of l.tokens) {
    const word = t.str.trim();
    for (const role of Object.keys(HEADER_WORDS) as Role[]) {
      if (HEADER_WORDS[role].test(word) && !bands.some(b => b.role === role)) {
        bands.push({ role, center: t.x + t.w / 2 });
      }
    }
  }
  return bands;
}

export interface ItemsResult {
  items: InvoiceItem[];
  arithmeticOk: boolean;
  warnings: string[];
}

export function extractItems(lines: Line[], subtotal: number | null, total: number | null): ItemsResult {
  const warnings: string[] = [];

  // 1) the header row: the line whose tokens name >= 2 known columns
  let headerIdx = -1;
  let bands: Band[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const b = headerBands(line);
    if (b.length >= 2) { headerIdx = i; bands = b.sort((a, c) => a.center - c.center); break; }
  }
  if (headerIdx < 0) return { items: [], arithmeticOk: false, warnings: ["No line-item header row found."] };

  // 2) item region: lines after the header until the totals block
  const region: Line[] = [];
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    if (STOP_RE.test(line.text)) break;
    if (line.text.trim()) region.push(line);
  }

  // 3) bucket tokens into the nearest band; model gets everything unclaimed
  const items: InvoiceItem[] = [];
  for (const line of region) {
    const buckets = new Map<Role, string[]>();
    const claim = (role: Role, s: string) => {
      const arr = buckets.get(role) ?? [];
      arr.push(s); buckets.set(role, arr);
    };
    for (const t of line.tokens) {
      const center = t.x + t.w / 2;
      let best: Band | null = null;
      let bestD = Infinity;
      for (const b of bands) {
        const d = Math.abs(center - b.center);
        if (d < bestD) { bestD = d; best = b; }
      }
      claim(best ? best.role : "model", t.str.trim());
    }

    const rawQty = (buckets.get("qty") ?? []).join(" ").match(/\d+/)?.[0];
    const rawPrice = (buckets.get("price") ?? []).join(" ");
    const rawSerial = (buckets.get("serial") ?? []).join("").replace(/\s+/g, "");
    let model = (buckets.get("model") ?? []).join(" ").replace(/\s{2,}/g, " ").trim();

    // a line with no money anywhere is not an item line (address rows, notes)
    const priceMatch = rawPrice.match(MONEY_RE)?.[0] ?? line.text.match(MONEY_RE)?.[0];
    if (!priceMatch) continue;
    const unitPrice = parseMoney(priceMatch);
    if (unitPrice === null) continue;

    // serial: prefer the serial bucket; else hunt the line for a serial-shaped token
    let serial: string | null = null;
    let serialConf = 0;
    if (SERIAL_RE.test(rawSerial)) { serial = rawSerial; serialConf = 0.9; }
    else {
      const hunt = line.tokens.map(t => t.str.trim()).find(s => SERIAL_RE.test(s) && parseMoney(s) === null);
      if (hunt) { serial = hunt; serialConf = 0.6; model = model.replace(hunt, "").replace(/\s{2,}/g, " ").trim(); }
    }

    if (!model) { warnings.push(`Item line with price ${priceMatch} had no readable description.`); continue; }

    items.push({
      qty: rawQty ? field(parseInt(rawQty, 10), 0.9, "text") : field(1, 0.5, "rule"),
      model: field(model, 0.85, "text"),
      serial: field(serial, serialConf, serial ? "text" : "rule"),
      unitPrice: field(unitPrice, 0.85, "text"),
    });
  }

  // 4) arithmetic self-check against the printed subtotal (or total when no tax line)
  const sum = items.reduce((s, it) => s + it.qty.value * it.unitPrice.value, 0);
  const target = subtotal ?? total;
  const arithmeticOk = target !== null && Math.abs(sum - target) <= 0.02;
  if (arithmeticOk) {
    for (const it of items) {
      it.qty = { ...it.qty, confidence: Math.max(it.qty.confidence, 0.95) };
      it.unitPrice = { ...it.unitPrice, confidence: 0.97 };
    }
  } else if (target !== null) {
    warnings.push(`Line items sum to ${sum.toFixed(2)} but the invoice prints ${target.toFixed(2)} — check for missed or extra lines.`);
  }

  return { items, arithmeticOk, warnings };
}
